import { Fragment, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router";
import { replace, map } from 'lodash';
import { Paths } from "../routes/Routes";
import { User } from "../../models/User";
import { renderPeriod, displayAcknowledgeDate } from "../../helpers/renderHelper";
import IncidentCard from "../common/IncidentCard";
import NoContent from "../common/NoContent";
import { Organization } from "../../models/Organization";
import { buildIncidents } from "../../helpers/incidentHelper";
import { useAppDispatch } from "../../redux/hooks";
import { clearBackNavigation } from "../../redux/navigationSlice";
import ScoreTile from "../common/ScoreTile";
import { AppMessages } from "../../strings";
import RankProgress from "../common/RankProgress";
import StreakSection from "../common/StreakSection";
import ConfirmAcknowledgmentModal from "./ConfirmAcknowledgmentModal";
import { useMutation } from '@apollo/client';
import { ACKNOWLEDGE_SCORECARD } from '../../graphQL/queries';
import { updateAcknowledgmentDate } from "../../redux/appStateSlice";
import { Area, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, ReferenceLine, ComposedChart, Line } from 'recharts';
import { formatLocalDate, findIdleDataForDate, getTodayAtMidnight, getLastSunday, getThisSaturday } from '../../helpers/dateHelper';

const DriverSummary = ({currentUser, currentOrganization}:{currentUser?: User, currentOrganization?:Organization}) => {

	const navigate = useNavigate()
	const dispatch = useAppDispatch()
	const clearAcknowledgment = false;
		
	// Check if organization has idle rule
	const hasIdleRule = currentOrganization?.goals?.some(goal => goal.goalTypeName === 'Idle');
	const showIdleChart = hasIdleRule;
	
	// Get idle goal target value from organization, fallback to 10
	const goal = currentOrganization?.goals?.find(g => g.goalTypeName === 'Idle')?.targetValue ?? 10; 

	const [showConfirmAcknowledgmentModal, setShowConfirmAcknowledgmentModal] = useState(false)

	const [confirmAcknowledgment, confirmAcknowledgmentResponses] = useMutation(ACKNOWLEDGE_SCORECARD);

	function navigateToIncidentDetail(e, incident) {
		const path = replace(Paths.DRIVER_INCIDENT_DETAIL, ':name', incident.name)
		navigate(path);
	}

	var	scorecard = currentUser?.scorecard
	const showAcknowledgeButton = clearAcknowledgment || (scorecard ? scorecard.currentScore && scorecard.acknowledgment !== null && scorecard.acknowledgment?.acknowledgedDate === null : false)
	const showAcknowledgedDate = scorecard ? scorecard.currentScore && scorecard.acknowledgment !== null && scorecard.acknowledgment?.acknowledgedDate !== null : false;

	// const incidents = flatMap(currentUser?.scorecard, sc => sc.incidents)
	const incidents = currentUser?.scorecard?.incidents
	const filteredIncidents = buildIncidents(incidents)
	const acknowledgedDate = scorecard?.acknowledgment ? scorecard?.acknowledgment?.acknowledgedDate : null

	// Chart data using real idle data and projections
	const chartData = useMemo(() => {
		// Get idle data from current user
		const idleData = currentUser?.idleInfo || [];
		const idleProjection = currentUser?.idleProjection;
		const dailyProjections = idleProjection?.dailyProjections || [];
		const weekProjection = idleProjection?.projectedIdlePercentage;

		// Get today's date for comparison (normalized to start of day)
		const today = getTodayAtMidnight();

		// Calculate date range: last Sunday through this Saturday
		const lastSunday = getLastSunday();
		const thisSaturday = getThisSaturday();
		
		// Create a map and add all dates from last Sunday through this Saturday
		const dateMap = new Map<string, {date: string, lastWeek?: number, projected?: number}>();
		
		// First, add all dates in the range to ensure they appear on the chart
		for (let i = 0; i < 14; i++) {
			const date = new Date(lastSunday);
			date.setDate(lastSunday.getDate() + i);
			const dateStr = date.toISOString().split('T')[0];
			const dateLabel = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
			dateMap.set(dateStr, { date: dateLabel });
		}
		
		
		// Add idle data
		idleData.forEach(item => {
			const dateStr = item.summaryDate.split('T')[0];
			if (dateMap.has(dateStr)) {
				dateMap.get(dateStr)!.lastWeek = item.idlePercentage;
			}
		});
		
		// Get today's date string for comparison (YYYY-MM-DD format)
		const todayStr = today.toISOString().split('T')[0];
		
		// Calculate yesterday's date string
		const yesterday = new Date(today);
		yesterday.setDate(today.getDate() - 1);
		const yesterdayStr = yesterday.toISOString().split('T')[0];
		
		// Get yesterday's actual idle data value for continuity
		const yesterdayIdleData = dateMap.get(yesterdayStr);
		const yesterdayHasActualData = yesterdayIdleData && yesterdayIdleData.lastWeek !== undefined && yesterdayIdleData.lastWeek !== null;
		
		// Add projection data only for today and future dates
		dailyProjections.forEach(proj => {
			const dateStr = proj.date.split('T')[0];
			
			// Compare date strings directly to avoid timezone issues
			// Only add projection if date is today or in the future, exists in the dateMap, and value is not null or 0
			if (dateStr >= todayStr && dateMap.has(dateStr) && proj.projectedIdlePercentage !== null && proj.projectedIdlePercentage !== 0) {
				dateMap.get(dateStr)!.projected = proj.projectedIdlePercentage;
			}
		});
		
		// Connect projection to yesterday's actual value for continuity
		// If yesterday has actual data and no projection, use yesterday's actual value as the starting point
		if (yesterdayHasActualData && dateMap.has(yesterdayStr) && !dateMap.get(yesterdayStr)!.projected) {
			dateMap.get(yesterdayStr)!.projected = yesterdayIdleData!.lastWeek;
		}
		
		// Convert map to array and sort by date string
		const data = Array.from(dateMap.entries())
			.sort((a, b) => a[0].localeCompare(b[0]))
			.map(entry => entry[1]);
		
		// Calculate Y-axis max: round up to next multiple of 5 that is >= max
		const allValues = data.map(d => d.lastWeek).filter(v => v !== undefined && v !== null);
		const allProjections = data.map(d => d.projected).filter(v => v !== undefined && v !== null);
		const dataMax = allValues.length > 0 ? Math.max(...allValues) : 0;
		const projectionMax = allProjections.length > 0 ? Math.max(...allProjections) : 0;
		const maxValue = Math.max(dataMax, projectionMax, goal, weekProjection || 0);
		const yAxisMax = Math.ceil(maxValue / 5) * 5;

		// Adjust displayed values if goal and weekProjection are too close
		let displayGoal = goal;
		let displayWeekProjection = weekProjection;
		if (weekProjection && Math.abs(weekProjection - goal) < 1.0) {
			const diff = Math.abs(weekProjection - goal);
			const offset = (0.3 - diff) / 2;

			// Move lines apart based on which is higher
			if (goal > weekProjection) {
				displayGoal = goal + offset;
				displayWeekProjection = weekProjection - offset;
			} else {
				displayGoal = goal - offset;
				displayWeekProjection = weekProjection + offset;
			}
		}

		return { data, yAxisMax, weekProjection, displayGoal, displayWeekProjection };
	}, [currentUser, goal]);

	useEffect(()=>{
		dispatch(clearBackNavigation())
	},[dispatch])

	useEffect(()=>{
		if (confirmAcknowledgmentResponses?.data) {
			dispatch(updateAcknowledgmentDate(confirmAcknowledgmentResponses?.data?.ack))
		}
	},[confirmAcknowledgmentResponses?.data, dispatch])


	function confirmAcknowledgmentClicked() {

		setShowConfirmAcknowledgmentModal(false);
		confirmAcknowledgment({
			variables: {
				acknowledgment: {
					acknowledgmentID: currentUser?.scorecard?.acknowledgment?.acknowledgmentID
				},
				clear: clearAcknowledgment
			}
		});
	}

	function promptToConfirmAcknowledgment(e) {
		e.preventDefault()
		setShowConfirmAcknowledgmentModal(true);
	}

	return (
		<Fragment>
			<div className="container" style={{marginTop:0, color:'white'}}>
				{ renderPeriod(currentOrganization) }
				{/* Scores */}
				<div className="row">
					<div className="col-12">
						<div className="card" style={{marginBottom: "0.35rem"}}>
							<div className="card-body align-items-center" style={{padding: "0.35rem"}}>
								<div className="row text-center g-0 p-0" >
									<ScoreTile score={scorecard} />
									<RankProgress score={scorecard} />
								</div>
							</div>
						</div>
					</div>
				</div>
				<StreakSection userStreaks={currentUser?.userStreaks} />
				{/* Acknowledge */}
				{ showAcknowledgeButton && 
					<div className="row">
						<div className="col-12">
						<div className="card" style={{marginBottom: "0.35rem"}}>
							<div className="card-body align-items-center" style={{padding:"0.35rem"}}>
							<div className="text-center">
								<form>
								<button className="w-100 btn btn-lg btn-primary text-dark" onClick={ e => promptToConfirmAcknowledgment(e) } >Acknowledge</button>
								</form>
							</div>
							</div>
						</div>
						</div>
					</div>
				}
				{ showAcknowledgedDate && 
					<div className="row">
						<div className="col-12 align-items-center">
						  <div className="card" style={{height:40}}>
						    <div className="card-body align-items-center" style={{padding: '0.35rem', display:'flex' }}>
							  <div className="fs-10 fst-italic mt-sm-0" style={{ alignSelf:'center', margin:'auto', color:'white'}} >{ acknowledgedDate && <i className="fa-solid fa-square-check icn-orange" style={{marginRight:10}}/> }{acknowledgedDate ? displayAcknowledgeDate(acknowledgedDate) ?? "Not Acknowledged" : "Not Required"}</div>
						    </div>
						  </div>
						</div>
					</div>
				}
				<div className="row">
					<div className="col-12">
						<div className="card" >
							<div className="card-body align-items-center" style={{padding: "0.35rem"}}>
								<div className="row g-0 p-0">
									{ (filteredIncidents.length > 0) ? (
										<Fragment>
											{
												map(filteredIncidents,(incident,index) => {
													return <IncidentCard key={`driver-553-${index}`} currentIncident={incident} onClick={e => navigateToIncidentDetail(e, incident)} />
												})
											}
										</Fragment>
									) : (
										<NoContent message={scorecard?.currentScore ? AppMessages.noIncidents : AppMessages.noDriverScore} showIcon={scorecard?.currentScore ? true : false} />
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
				
				{/* Area Chart Section - Only show when idle parameter is present */}
				{showIdleChart && (
					<div className="row" key={`idle-chart-${currentUser?.userID}`}>
						<div className="col-12">
							<div className="card">
								<div className="card-body" style={{padding: "0.35rem"}}>
									<h5 className="card-title mb-2 text-center">Idle Time (%)</h5>
									<div style={{ width: '100%', height: 280 }} key={`chart-container-${currentUser?.userID}`}>
										<ResponsiveContainer width="100%" height="100%">
											<ComposedChart
												data={chartData.data}
												key={`composed-chart-${currentUser?.userID}`}
												margin={{
													top: 5,
													right: 10,
													left: -10,
													bottom: 0,
												}}
											>
												<defs>
													<linearGradient id="blueGradient" x1="0" y1="0" x2="0" y2="1">
														<stop offset="0%" stopColor="#3498db" stopOpacity={0.8}/>
														<stop offset="100%" stopColor="#3498db" stopOpacity={0}/>
													</linearGradient>
													<linearGradient id="orangeGradient" x1="0" y1="0" x2="0" y2="1">
														<stop offset="0%" stopColor="#f39c12" stopOpacity={0.6}/>
														<stop offset="100%" stopColor="#f39c12" stopOpacity={0}/>
													</linearGradient>
												</defs>
												<CartesianGrid strokeDasharray="3 3" stroke="#333" />
												<XAxis 
													dataKey="date" 
													stroke="#fff"
													fontSize={12}
													tick={{ fill: '#fff' }}
													tickLine={false}
													tickSize={0}
													tickMargin={0}
													interval={0}
													angle={-45}
													textAnchor="end"
													height={60}
													axisLine={false}
													allowDuplicatedCategory={false}
												/>
												<YAxis
													stroke="#fff"
													fontSize={12}
													tick={{ fill: '#fff' }}
													domain={[0, chartData.yAxisMax]}
													ticks={Array.from({length: Math.floor(chartData.yAxisMax / 5) + 1}, (_, i) => i * 5)}
													interval={0}
													tickLine={{ stroke: '#fff', strokeWidth: 1 }}
												/>
												<Tooltip
													contentStyle={{
														backgroundColor: '#2c3e50',
														border: '1px solid #34495e',
														borderRadius: '6px',
														color: '#fff'
													}}
													content={({ active, payload }) => {
														if (active && payload && payload.length) {
															// Only show items with specific names we want to display
															let filteredPayload = payload.filter(item =>
																item.name === 'Idle %' || item.name === 'Projected'
															);

															// If both "Idle %" and "Projected" exist, hide "Projected"
															const hasIdlePercent = filteredPayload.some(item => item.name === 'Idle %' && item.value !== null && item.value !== undefined);
															const hasProjected = filteredPayload.some(item => item.name === 'Projected' && item.value !== null && item.value !== undefined);

															if (hasIdlePercent && hasProjected) {
																filteredPayload = filteredPayload.filter(item => item.name !== 'Projected');
															}

															return (
																<div style={{
																	backgroundColor: '#2c3e50',
																	border: '1px solid #34495e',
																	borderRadius: '6px',
																	padding: '10px'
																}}>
																	<p style={{ margin: 0, color: '#fff' }}>{payload[0].payload.date}</p>
																	{filteredPayload.map((entry, index) => (
																		<p key={index} style={{ margin: '5px 0 0 0', color: entry.color }}>
																			{entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}%
																		</p>
																	))}
																</div>
															);
														}
														return null;
													}}
												/>
												{chartData.weekProjection && chartData.weekProjection > 0 && (
													<ReferenceLine
														y={chartData.displayWeekProjection}
														stroke="#f39c12"
														strokeWidth={1}
													/>
												)}
												<Line
													type="linear"
													dataKey="projected"
													stroke="#3498db"
													strokeDasharray="5 5"
													strokeWidth={1}
													fill="none"
													name="Projected"
													connectNulls={false}
													dot={(props) => {
														// Don't render dot if value is null or undefined
														if (props.value === null || props.value === undefined) {
															return <g key={`projected-dot-${props.index}`} />;
														}

														// Calculate yesterday's date label
														const today = new Date();
														today.setHours(0, 0, 0, 0);
														const yesterday = new Date(today);
														yesterday.setDate(today.getDate() - 1);
														const yesterdayLabel = yesterday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

														// Get the date label from the data point
														const dataDate = props.payload?.date;
														if (!dataDate) {
															return <g key={`projected-dot-${props.index}`} />;
														}

														// Hide dot if this is yesterday's date (the connection point)
														if (dataDate === yesterdayLabel) {
															return <g key={`projected-dot-${props.index}`} />;
														}

														return <circle key={`projected-dot-${props.index}`} cx={props.cx} cy={props.cy} r={3} fill="#3498db" />;
													}}
													activeDot={{ r: 4 }}
												/>
												<Area
													type="linear"
													dataKey="lastWeek"
													stroke="#3498db"
													strokeWidth={2}
													fill="url(#blueGradient)"
													name="Idle %"
													connectNulls={false}
													dot={(props) => {
														// Don't render dot if value is null, undefined, or invalid
														if (props.value === null || props.value === undefined || props.payload?.lastWeek === null || props.payload?.lastWeek === undefined) {
															return <circle key={`lastWeek-dot-${props.index}`} cx={props.cx} cy={props.cy} r={0} fill="none" />;
														}
														return <circle key={`lastWeek-dot-${props.index}`} cx={props.cx} cy={props.cy} r={3} fill="#3498db" />;
													}}
												/>
												<ReferenceLine
													y={chartData.displayGoal}
													stroke="#2ecc71"
													strokeWidth={1}
												/>
											</ComposedChart>
										</ResponsiveContainer>
									</div>
									<div className="d-flex justify-content-center gap-4 mt-2">
										<div className="d-flex align-items-center gap-2">
											<div style={{ width: '30px', height: '1px', backgroundColor: '#2ecc71' }}></div>
											<span style={{ color: 'white', fontSize: '0.875rem' }}>Goal: {goal}%</span>
										</div>
										{chartData.weekProjection && chartData.weekProjection > 0 && (
											<div className="d-flex align-items-center gap-2">
												<div style={{ width: '30px', height: '1px', backgroundColor: '#f39c12' }}></div>
												<span style={{ color: 'white', fontSize: '0.875rem' }}>Projected: {chartData.weekProjection.toFixed(1)}%</span>
											</div>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
				)}
			</div>
			{ showConfirmAcknowledgmentModal && <ConfirmAcknowledgmentModal shouldShow={showConfirmAcknowledgmentModal} onConfirmAcknowledgment={()=>confirmAcknowledgmentClicked()} onClose={()=>setShowConfirmAcknowledgmentModal(false) } />}

		</Fragment>
	)
}

export default DriverSummary;